import React, { FunctionComponent } from 'react';

import styled from 'styled-components';

import { showMainLoader } from '@webapp/store/ui/selectors';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import ComingSoonPill from '@components/basics/coming-soon-pill';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';

export interface TabProp<T = string> {
  label: React.ReactNode;
  key: T;
  hasDot?: boolean;
  disabled?: boolean;
  soon?: boolean;
  isTrack?: boolean;
}

interface TabsProps {
  className?: string;
  tabs: TabProp[];
  currentTab: number;
  bigFont?: boolean;
  activeBigFont?: boolean;
  overflow?: boolean;
  loading?: boolean;
  isMobile: boolean;
  onChange: (data: TabProp) => void;
}

const mapStatetoProps = (state: AppState) => ({
  loading: showMainLoader(state),
  isMobile: state.ui.mobile,
});

const TabsNav: FunctionComponent<TabsProps> = ({
  className,
  tabs,
  currentTab,
  bigFont,
  activeBigFont,
  overflow,
  isMobile,
  onChange,
}) => {
  React.useEffect(() => {
    if (tabs && currentTab && !isMobile) {
      const element = document.getElementById(`item-${currentTab}`);
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [tabs, currentTab, isMobile]);

  const handleClick = (tab: TabProp) => {
    onChange(tab);
    if (isMobile) {
      return;
    }
    const element = document.getElementById(`item-${tabs.indexOf(tab)}`);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  };

  return (
    <MotionOpacity>
      <Tabs
        className={`${className || ''} ${overflow ? 'overflow' : ''}`}
        alignItems="center"
      >
        {tabs.map((tab, index) => (
          <TabWrapper
            key={index}
            className={`tab ${
              index === currentTab ? 'active-tab-wrapper' : ''
            }`}
          >
            <Tab
              className={`${index === currentTab ? 'active' : ''} ${
                bigFont ? 'big' : ''
              } ${tab.hasDot && !tab.soon ? 'dot' : ''} ${
                tab.disabled ? 'disabled' : ''
              } ${activeBigFont ? 'active-big' : ''}`}
              id={`item-${index}`}
              onClick={() => (tab.disabled ? void 0 : handleClick(tab))}
              weight="700"
              customFont={bigFont ? 'semi-bold' : undefined}
            >
              {tab.label}
            </Tab>
            {tab.hasDot && !tab.soon ? <Dot /> : null}
            {tab.soon ? <ComingSoonPill /> : null}
          </TabWrapper>
        ))}
      </Tabs>
    </MotionOpacity>
  );
};

export default connect(mapStatetoProps)(TabsNav);

const Tabs = styled(Flex)`
  gap: 20px;
  @media ${({ theme }) => theme.device.tablet} {
    gap: 30px;
  }

  &.overflow {
    overflow-x: scroll;
    padding-block-end: 20px;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .tab {
      flex-shrink: 0;
    }
  }
`;

const Tab = styled(Typography)`
  opacity: 0.5;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.h3};
  line-height: normal;
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover,
  &:active,
  &.active {
    opacity: 1;
  }

  &.big {
    font-size: ${({ theme }) => theme.typography.h2};
  }

  &.active-big {
    font-size: ${({ theme }) => theme.typography.h4};
    &.active {
      font-size: ${({ theme }) => theme.typography.h2};
    }
  }

  &.dot {
    padding-right: 8px;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.25;
  }

  @media ${({ theme }) => theme.device.tablet} {
    font-size: ${({ theme }) => theme.typography.sectionTitle};

    &.big {
      font-size: ${({ theme }) => theme.typography.h2};
    }
  }
`;

const TabWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const Dot = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.accent};
  width: 6px;
  height: 6px;
  border-radius: 50%;
  isolation: isolate;
  transform: translate(0, 100%);
`;
